import { Injectable, signal } from '@angular/core';
import { Store } from "@ngrx/store";
import { selectPermissions } from "../store/user/permissions/permissions.selectors";
import { ShelterAppStateModel } from "../store/ShelterAppStateModel";
import _ from 'lodash';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { appConfig } from "../../shelter.config";
import { Observable } from "rxjs";
import { logout } from "../store/auth/auth.actions";
import { setPermissions } from "../store/user/permissions/permissions.actions";


// ADMIN
export const adminPermissions = {
  read: 'petszel-connect:read',
  write: 'petszel-connect:write'
};

// DASHBOARD
export const dashboardPermissions = {
  read: 'petszel-connect:dashboard:read'
};

// ORG PROFILE
export const orgProfileBusinessInfoPermissions = {
  read: 'orgprofile:businessinfo:read',
  write: 'orgprofile:businessinfo:write'
};
export const orgProfileContactInfoPermissions = {
  read: 'orgprofile:contactinfo:read',
  write: 'orgprofile:contactinfo:write'
};
export const orgProfileSitesPermissions = {
  read: 'orgprofile:sites:read',
  write: 'orgprofile:sites:write'
};
export const orgProfileLogoPermissions = {
  all: 'orgprofile:logo:all',
  read: 'orgprofile:logo:read',
  write: 'orgprofile:logo:write'
};
export const orgProfileWebSocialMediaPermissions = {
  all: 'orgprofile:websocialmedia:all',
  read: 'orgprofile:websocialmedia:read',
  write: 'orgprofile:websocialmedia:write'
};

// USER MANAGEMENT
export const userManagementPermissions = {
  all: 'team:usermanagement:all',
  read: 'team:usermanagement:read',
  write: 'team:usermanagement:write'
};

// INTEGRATION
export const integrationSoftwarePermissions = {
  read: 'integration:software:read',
  // temp changed to create instead of write
  write: 'integration:software:create'
};
export const integrationPartnerPermissions = {
  read: 'integration:partner:read',
  // temp changed to create instead of write
  write: 'integration:partner:create'
};

// ADOPTION PACKET
export const adoptionPacketPermissions = {
  all: 'petszelconnect:adoptionpacket:all',
  read: 'petszelconnect:adoptionpacket:read',
  write: 'petszelconnect:adoptionpacket:write'
};

// LOCAL PARTNERS || PROVIDERS
export const providersPermissions = {
  all: 'petszelconnect:localpartners:all',
  read: 'petszelconnect:localpartners:read',
  write: 'petszelconnect:localpartners:write',
  export: 'petszelconnect:localpartners:export'
};

// SURVEYS
export const surveysPermissions = {
  read: 'data:survey:read',
  export: 'data:survey:export'
};

// ADOPTIONS
export const adopterHubAdoptionsPermissions = {
  all: 'adopterhub:adoptions:all',
  read: 'adopterhub:adoptions:read',
  write: 'adopterhub:adoptions:write'
};

export const adopterHubPetParentSearchPermissions = {
  read: 'adopterhub:petparentsearch:read'
};

export const adopterHubParentProfilePermissions = {
  all: 'adopterhub:parentprofile:all',
  read: 'adopterhub:parentprofile:read',
  write: 'adopterhub:parentprofile:write'
};

export const adopterHubPetProfilePermissions = {
  all: 'adopterhub:petprofile:all',
  read: 'adopterhub:petprofile:read',
  write: 'adopterhub:petprofile:write'
};

export interface CurrentUserModel {
  accountId: string;
  credentialId: string;
  firstname: string;
  lastname: string;
  email: string;
  phoneNumber: {
    phoneNumber: string;
    isMobile: boolean;
    okayToSendSMS: boolean;
    description: string;
  }[];
  accessLevel: string;
  allowedLocations: string[];
  allowedSpecies: string[];
  userStatus: number;
  jobTitle: string;
  userId: string;
  invitationToken: string;
  inviteAccepted: boolean;
  invitationTokenExpiry: string;
  createdDate: string;
  lastUpdatedDate: string;
  credentialTypes: number[];
  credentialScopes: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PermissionsHandlerService {

  private token = localStorage.getItem('token');
  private headers = new HttpHeaders().set('Authorization', `Bearer ${this.token}`);
  private readonly navItems = [
    {
      label: 'Dashboard',
      routerLink: '/shelter-dashboard',
      icon: 'dashboardIcon',
      exact: true,
      permissions: [
        ...Object.values(adminPermissions),
        ...Object.values(dashboardPermissions)
      ]
    },{
      label: 'Adoptions',
      routerLink: '/adoptions-list',
      icon: 'adoptionListIcon',
      exact: true,
      permissions: [
        ...Object.values(adminPermissions),
        ...Object.values(adopterHubAdoptionsPermissions)
      ]
    },
    {
      label: 'Check-Ins',
      routerLink: '/survey',
      icon: 'checkInsIcon',
      permissions: [
        ...Object.values(adminPermissions),
        ...Object.values(surveysPermissions)
      ]
    },
    {
      label: 'Org Profile',
      routerLink: '/administration',
      icon: 'orgProfileIcon',
      permissions: [
        ...Object.values(adminPermissions),
        ...Object.values(orgProfileBusinessInfoPermissions),
        ...Object.values(orgProfileContactInfoPermissions),
        ...Object.values(orgProfileSitesPermissions),
        ...Object.values(orgProfileLogoPermissions),
        ...Object.values(orgProfileWebSocialMediaPermissions)
      ]
    },
    {
      label: 'User Management',
      routerLink: '/user-management',
      icon: 'userManagementIcon',
      permissions: [
        ...Object.values(adminPermissions),
        ...Object.values(userManagementPermissions)
      ]
    },
    {
      label: 'Integrations',
      routerLink: '/integrations',
      icon: 'integrationsIcon',
      permissions: [
        ...Object.values(adminPermissions),
        ...Object.values(integrationSoftwarePermissions),
        ...Object.values(integrationPartnerPermissions)
      ]
    },
    {
      label: 'Packet',
      routerLink: '/adoptions-packet',
      icon: 'adoptionPacketIcon',
      permissions: [
        ...Object.values(adminPermissions),
        ...Object.values(adoptionPacketPermissions)
      ]
    },
    {
      label: 'Providers',
      routerLink: '/providers',
      icon: 'providersIcon',
      permissions: [
        ...Object.values(adminPermissions),
        ...Object.values(providersPermissions)
      ]
    },
  ];
  private userPermissions = signal<string[] | null>(null);

  constructor(private store: Store<ShelterAppStateModel>,
              private http: HttpClient) {
    this.store.select(selectPermissions).subscribe(permissions => {
      this.userPermissions.set(permissions);
    });
  }

  setUserPermissionsAndScopes(user: CurrentUserModel) {
    this.store.dispatch(setPermissions({
      permissions: user.credentialScopes,
      locations: (user.allowedLocations && user.allowedLocations.length > 0) ? user.allowedLocations : ['all'],
      species: (user.allowedSpecies && user.allowedSpecies.length > 0) ? user.allowedSpecies : ['all']
    }));
  }

  manageUserPermissionsAndScopes(): void {
    this.loadCurrentUser().subscribe({
      next: (user) => {
        this.setUserPermissionsAndScopes(user);
      },
      error: () => this.store.dispatch(logout())
    });

  }

  loadCurrentUser(): Observable<CurrentUserModel> {
    const url = `${appConfig.apiUrl}Shelter/Users/Current`;
    return this.http.get<CurrentUserModel>(url, { headers: this.headers });
  }

  buildNavigationItems(isAdopterHubEnabled: boolean): any[] {
    let nav: any[] = _.cloneDeep(this.navItems);
    nav = this.filterOutAdopterHub(nav, isAdopterHubEnabled);

    return nav.filter(i => {
      if (i.permissions && i.permissions.length > 0) {
        return this.hasAnyOf(i.permissions);
      }

      return true;
    });
  }

  private filterOutAdopterHub(nav: any[],isAdopterHubEnabled: boolean): any[] {
    if (!isAdopterHubEnabled) {
      return nav.filter(i => i.label !== 'Adoptions');
    }

    return nav;
  }

  private hasAnyOf(permissions: string[]): boolean {
    return !!this.userPermissions()?.some(p => permissions.includes(p));
  }
}
